import { STRUCTURING_API } from './structuringUtils';
import { toast } from 'react-toastify';
import { deleteInstallment, deleteDocument } from '../../../services/StructuredOperationsService';

export type Receivable = {
    receivable_number: string;
    due_date: string;
    amount: number;
    v_pag?: string;
    nat_op?: string;
    ind_pag?: string;
};

export type Document = {
    assignor_name: string;
    assignor_cnpj: string;
    document_id: string;
    document_title: string;
    cfop: string;
    issue_date: string;
    xml_size_bytes: number;
    receivables: Receivable[];
    debtor_cpf_cnpj: string;
    debtor_name: string;
    debtor_email: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_district: string;
    address_ibge_code: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
    address_country: string;
    address_phone: string;
};

export type Operation = {
    operation_code: string;
    total_documents: number;
    total_receivables: number;
    total_amount: number;
    processing_date: string;
    s3_location: string;
    documents: Document[];
    notes: string;
    status?: string;
};

export const buildFetchUrl = (id: string | undefined): string => {
    return `${STRUCTURING_API.url}/operations/${id}`;
};

export const buildFetchHeaders = () => [
    { key: 'X-API-Key', value: STRUCTURING_API.key }
];

export const parseOperationData = (data: any): Operation => {
    const operationCode = data.operation?.operation_code || data.operation_code || '';
    const totalAmount = data.operation?.total_amount || data.total_amount || 0;
    const totalReceivables = (data.documents || []).reduce(
        (acc: number, doc: Document) => acc + (doc.receivables?.length || 0),
        0
    );

    return {
        operation_code: operationCode,
        total_documents: data.operation?.total_documents || data.total_documents || 0,
        total_receivables: totalReceivables,
        total_amount: totalAmount,
        processing_date: data.operation?.processing_date || data.processing_date || '',
        s3_location: data.operation?.s3_location || data.s3_location || '',
        documents: data.documents || [],
        notes: data.notes || '',
        status: data.operation?.status || data.status || ''
    };
};

export const reloadOperationData = async (id: string | undefined): Promise<Operation | null> => {
    try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(buildFetchUrl(id), {
            headers: {
                'X-API-Key': STRUCTURING_API.key,
                'Authorization': `Bearer ${token}`,
            }
        });

        if (response.ok) {
            const newData = await response.json();
            return parseOperationData(newData);
        }
        return null;
    } catch (error) {
        console.error('Error reloading data:', error);
        return null;
    }
};

export const handleDeleteDocument = async (
    operationCode: string,
    documentId: string
): Promise<{ success: boolean; operationDeleted: boolean; message: string }> => {
    try {
        const request = {
            action: 'delete_document' as const,
            operation_code: operationCode,
            document_id: documentId
        };

        const response = await deleteDocument(request);
        toast.success(response.message);

        return {
            success: true,
            operationDeleted: response.operation_deleted || false,
            message: response.message
        };
    } catch (error: any) {
        console.error('Delete error:', error);
        toast.error(error.message || 'Erro ao deletar');
        return {
            success: false,
            operationDeleted: false,
            message: error.message || 'Erro ao deletar'
        };
    }
};

export const handleDeleteInstallment = async (
    operationCode: string,
    documentId: string,
    installmentNumber: string
): Promise<{ success: boolean; message: string }> => {
    try {
        const request = {
            action: 'delete_installment' as const,
            operation_code: operationCode,
            document_id: documentId,
            installment_number: installmentNumber
        };

        const response = await deleteInstallment(request);
        toast.success(response.message);

        return {
            success: true,
            message: response.message
        };
    } catch (error: any) {
        console.error('Delete error:', error);
        toast.error(error.message || 'Erro ao deletar');
        return {
            success: false,
            message: error.message || 'Erro ao deletar'
        };
    }
};

export const buildTableHeaders = () => [
    { id: 'document_title', title: 'Título', field: 'document_title', canSort: false, width: '200px' },
    { id: 'receivable_number', title: 'Parcela', field: 'receivable_number', canSort: false, width: '100px', class: 'text-center' },
    { id: 'due_date', title: 'Vencimento', field: 'due_date', canSort: false, width: '120px', class: 'text-center' },
    { id: 'amount', title: 'Valor', field: 'amount', canSort: false, width: '120px', class: 'text-center' },
    { id: 'cedente_name', title: 'Cedente', field: 'assignor_name', canSort: false, width: '200px' },
    { id: 'debtor_name', title: 'Sacado', field: 'debtor_name', canSort: false, width: '200px' },
    { id: 'actions', title: 'Ações', field: 'actions', canSort: false, width: '100px', class: 'text-center' }
];

export const expandDocumentsToRows = (documents: Document[]) => {
    const expandedData: any[] = [];

    documents.forEach(document => {
        document.receivables.forEach((receivable) => {
            expandedData.push({
                document_title: document.document_title,
                document_id: document.document_id,
                receivable_number: receivable.receivable_number,
                due_date: receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '',
                amount: receivable.amount,
                assignor_name: document.assignor_name,
                debtor_name: document.debtor_name,
                receivable_raw: receivable,
                document_raw: document
            });
        });
    });

    return expandedData;
};

export const paginateData = (data: any[], currentPage: number, itemsPerPage: number) => {
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = data.slice(startIndex, endIndex);

    return {
        paginatedData,
        totalItems,
        totalPages,
        startIndex,
        endIndex
    };
};

export const saveDocuments = async (payload: { documents: any[] }): Promise<{ success: boolean; message: string }> => {
    try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${STRUCTURING_API.url}/documents/save`, {
            method: 'POST',
            headers: {
                'X-API-Key': STRUCTURING_API.key,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok) {
            return { success: true, message: data.message || 'Documentos salvos com sucesso' };
        }
        return { success: false, message: data.message || 'Erro ao salvar documentos' };
    } catch (error) {
        console.error('Erro ao salvar documentos:', error);
        return { success: false, message: 'Erro de conexão ao salvar documentos' };
    }
};
